/**
 * Forgot Password Page
 * Handles password reset request
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import FormInput from '../../components/auth/FormInput';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import { validateForgotPasswordForm, hasErrors } from '../../utils/validation';
import { forgotPassword } from '../../services/authService';
import { FormErrors, ApiError } from '../../types/auth';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  // Form state
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setError(null);

    // Validate form
    const errors = validateForgotPasswordForm({ email });
    if (hasErrors(errors)) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || t('auth:forgotPasswordPage.sendFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message
  if (success) {
    return (
      <AuthLayout
        title={t('auth:forgotPasswordPage.sentTitle')}
        subtitle={t('auth:forgotPassword.subtitle')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('auth:forgotPasswordPage.sentTitle')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('auth:forgotPasswordPage.sentDescription')}
            </p>
            <p className="text-sm text-gray-500">
              {t('auth:register.didntReceive')}{' '}
              <button
                onClick={() => {
                  setSuccess(false);
                }}
                className="text-primary-600 hover:underline"
              >
                {t('auth:register.resendEmail')}
              </button>
            </p>
          </div>

          <Link to="/login">
            <Button fullWidth variant="outline">
              {t('auth:forgotPasswordPage.backToLogin')}
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth:forgotPassword.title')}
      subtitle={t('auth:forgotPassword.subtitle')}
    >
      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <FormInput
          label={t('auth:forgotPassword.email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={formErrors.email}
          placeholder={t('auth:placeholders.email')}
          autoComplete="email"
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          isLoading={isLoading}
          disabled={isLoading}
        >
          {t('auth:forgotPassword.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          <span className="inline-flex items-center">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('auth:forgotPassword.backToLogin')}
          </span>
        </Link>
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
